
import React, { useState } from 'react';
import { useData } from '../../context/DataContext';
import { Save, Server, Shield, Globe, LayoutTemplate, Plus, Trash2, Edit } from 'lucide-react';
import { EmailTemplate } from '../../types';

const EmailConfiguration: React.FC = () => {
  const { emailConfig, updateEmailConfig, emailTemplates, saveEmailTemplate } = useData();
  const [activeTab, setActiveTab] = useState<'smtp' | 'templates'>('smtp');
  
  // SMTP Form State
  const [smtpForm, setSmtpForm] = useState(emailConfig);
  
  // Template Form State
  const [editingTemplate, setEditingTemplate] = useState<Partial<EmailTemplate> | null>(null);

  const handleSmtpSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateEmailConfig(smtpForm);
    alert("SMTP Configuration Saved!");
  };

  const handleTemplateSave = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingTemplate && editingTemplate.name) {
      const newTemplate: EmailTemplate = {
        id: editingTemplate.id || Math.random().toString(36).substr(2, 9),
        name: editingTemplate.name!,
        subject: editingTemplate.subject || '',
        body: editingTemplate.body || '',
        variables: editingTemplate.variables || []
      };
      saveEmailTemplate(newTemplate);
      setEditingTemplate(null);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Email Configuration</h1>
        <p className="text-gray-500">Configure SMTP settings and manage email templates.</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('smtp')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'smtp' ? 'border-automax-red text-automax-red' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
          >
            SMTP Settings
          </button>
          <button
            onClick={() => setActiveTab('templates')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'templates' ? 'border-automax-red text-automax-red' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
          >
            Email Templates
          </button>
        </nav>
      </div>

      {activeTab === 'smtp' && (
        <form onSubmit={handleSmtpSubmit} className="bg-white p-8 rounded-sm shadow-sm border border-gray-100 space-y-8 max-w-4xl">
          <section className="space-y-4">
            <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2"><Server size={20}/> Server Configuration</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">SMTP Host</label>
                <input required type="text" value={smtpForm.smtpHost} onChange={e => setSmtpForm({...smtpForm, smtpHost: e.target.value})} className="w-full border p-3 rounded-sm bg-gray-50" placeholder="smtp.gmail.com" />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">SMTP Port</label>
                <input required type="number" value={smtpForm.smtpPort} onChange={e => setSmtpForm({...smtpForm, smtpPort: Number(e.target.value)})} className="w-full border p-3 rounded-sm bg-gray-50" placeholder="587" />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Encryption</label>
                <select value={smtpForm.encryption} onChange={e => setSmtpForm({...smtpForm, encryption: e.target.value as any})} className="w-full border p-3 rounded-sm bg-gray-50">
                  <option value="TLS">TLS</option>
                  <option value="SSL">SSL</option>
                  <option value="None">None</option>
                </select>
              </div>
            </div>
          </section>

          <section className="space-y-4">
            <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2"><Shield size={20}/> Authentication</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Username / Email</label>
                <input required type="text" value={smtpForm.username} onChange={e => setSmtpForm({...smtpForm, username: e.target.value})} className="w-full border p-3 rounded-sm bg-gray-50" />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Password</label>
                <input type="password" value={smtpForm.password || ''} onChange={e => setSmtpForm({...smtpForm, password: e.target.value})} className="w-full border p-3 rounded-sm bg-gray-50" placeholder="••••••••" />
              </div>
            </div>
          </section>

           <section className="space-y-4">
            <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2"><Globe size={20}/> Sender Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Sender Name</label>
                <input required type="text" value={smtpForm.senderName} onChange={e => setSmtpForm({...smtpForm, senderName: e.target.value})} className="w-full border p-3 rounded-sm bg-gray-50" />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Sender Email</label>
                <input required type="email" value={smtpForm.senderEmail} onChange={e => setSmtpForm({...smtpForm, senderEmail: e.target.value})} className="w-full border p-3 rounded-sm bg-gray-50" />
              </div>
               <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Daily Email Limit</label>
                <input required type="number" value={smtpForm.dailyLimit} onChange={e => setSmtpForm({...smtpForm, dailyLimit: Number(e.target.value)})} className="w-full border p-3 rounded-sm bg-gray-50" />
              </div>
            </div>
          </section>

          <div className="pt-4">
            <button type="submit" className="bg-automax-red text-white px-6 py-3 rounded-sm font-bold flex items-center gap-2 hover:bg-red-700 transition-colors">
              <Save size={20} /> Save Configuration
            </button>
          </div>
        </form>
      )}

      {activeTab === 'templates' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
           {/* List */}
           <div className="lg:col-span-1 space-y-4">
             <button 
               onClick={() => setEditingTemplate({ name: '', subject: '', body: '', variables: [] })}
               className="w-full py-3 border-2 border-dashed border-gray-300 text-gray-500 font-bold rounded-sm hover:border-automax-red hover:text-automax-red transition-colors flex items-center justify-center gap-2"
             >
               <Plus size={20} /> Create New Template
             </button>
             
             <div className="space-y-2">
               {emailTemplates.map(t => (
                 <div key={t.id} onClick={() => setEditingTemplate(t)} className={`p-4 rounded-sm border cursor-pointer transition-all ${editingTemplate?.id === t.id ? 'border-automax-red bg-red-50' : 'border-gray-200 bg-white hover:border-gray-300'}`}>
                   <div className="font-bold text-gray-900">{t.name}</div>
                   <div className="text-xs text-gray-500 mt-1">{t.subject}</div>
                 </div>
               ))}
             </div>
           </div>

           {/* Editor */}
           <div className="lg:col-span-2">
             {editingTemplate ? (
               <form onSubmit={handleTemplateSave} className="bg-white p-6 rounded-sm shadow-sm border border-gray-100 space-y-4">
                 <h3 className="font-bold text-gray-900 flex items-center gap-2 pb-2 border-b">
                   <LayoutTemplate size={20} /> {editingTemplate.id ? 'Edit Template' : 'New Template'}
                 </h3>
                 <div>
                   <label className="block text-sm font-bold text-gray-700 mb-1">Template Name</label>
                   <input required type="text" value={editingTemplate.name} onChange={e => setEditingTemplate({...editingTemplate, name: e.target.value})} className="w-full border p-2 rounded-sm" />
                 </div>
                 <div>
                   <label className="block text-sm font-bold text-gray-700 mb-1">Email Subject</label>
                   <input required type="text" value={editingTemplate.subject} onChange={e => setEditingTemplate({...editingTemplate, subject: e.target.value})} className="w-full border p-2 rounded-sm" />
                 </div>
                 <div>
                   <label className="block text-sm font-bold text-gray-700 mb-1">HTML Body</label>
                   <textarea required rows={10} value={editingTemplate.body} onChange={e => setEditingTemplate({...editingTemplate, body: e.target.value})} className="w-full border p-2 rounded-sm font-mono text-sm"></textarea>
                   <p className="text-xs text-gray-500 mt-2">Available Variables: {['{name}', '{email}', '{truck}', '{price}'].join(', ')}</p>
                 </div>
                 <div className="flex justify-end gap-2 pt-2">
                   <button type="button" onClick={() => setEditingTemplate(null)} className="px-4 py-2 text-gray-600 hover:text-gray-900">Cancel</button>
                   <button type="submit" className="bg-automax-red text-white px-6 py-2 rounded-sm font-bold">Save Template</button>
                 </div>
               </form>
             ) : (
               <div className="h-full flex items-center justify-center text-gray-400 bg-gray-50 rounded-sm border border-gray-200 p-12">
                 Select a template to edit or create a new one.
               </div>
             )}
           </div>
        </div>
      )}
    </div>
  );
};

export default EmailConfiguration;
